import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

interface TenantContextResult {
  defaultTenantId: string | undefined
  isGlobalAdmin: boolean
  userTenants: any[]
  isLoading: boolean
  shouldShowAllTenants: boolean
  getDefaultTenantId: () => string | undefined
}

const TENANT_STORAGE_KEY = 'guardian_selected_tenant'

export function useTenantContext(): TenantContextResult {
  const [defaultTenantId, setDefaultTenantId] = useState<string | undefined>(undefined)
  
  // Get current user to determine role
  const { data: currentUser, isLoading: userLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: api.getCurrentUser,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  })

  // Get accessible tenants
  const { data: tenants = [], isLoading: tenantsLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: api.getTenants,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  })

  const isGlobalAdmin = currentUser?.isGlobalAdmin || currentUser?.role === 'GlobalAdmin'
  const isLoading = userLoading || tenantsLoading

  // Determine default tenant on initial load
  useEffect(() => {
    if (isLoading) return

    // Check if there's a stored preference
    const storedTenant = localStorage.getItem(TENANT_STORAGE_KEY)
    
    // For GlobalAdmin users
    if (isGlobalAdmin) {
      // If they have a stored preference and it's valid, use it
      if (storedTenant === 'all' || (storedTenant && tenants.some(t => t.tenantId === storedTenant))) {
        setDefaultTenantId(storedTenant === 'all' ? undefined : storedTenant)
      } else {
        // Default to "All Tenants" for GlobalAdmin
        setDefaultTenantId(undefined)
      }
    } else {
      // For customer users
      if (tenants.length === 1) {
        // If they only have access to one tenant, use it
        setDefaultTenantId(tenants[0].tenantId)
      } else if (tenants.length > 1) {
        // If they have multiple tenants, check for stored preference
        if (storedTenant && tenants.some(t => t.tenantId === storedTenant)) {
          setDefaultTenantId(storedTenant)
        } else {
          // Default to their first accessible tenant
          setDefaultTenantId(tenants[0].tenantId)
        }
      }
      // If no tenants, defaultTenantId stays undefined
    }
  }, [isLoading, isGlobalAdmin, tenants])

  // Helper function to get the default tenant ID
  const getDefaultTenantId = () => {
    // For GlobalAdmin, undefined means "All Tenants"
    if (isGlobalAdmin) {
      return defaultTenantId // Can be undefined (All Tenants) or specific tenant
    }
    
    // For customer users, always return a specific tenant ID
    if (tenants.length > 0) {
      return defaultTenantId || tenants[0].tenantId
    }
    
    return undefined
  }

  return {
    defaultTenantId,
    isGlobalAdmin,
    userTenants: tenants,
    isLoading,
    shouldShowAllTenants: isGlobalAdmin && tenants.length > 1,
    getDefaultTenantId,
  }
}

// Export helper to save tenant selection
export function saveTenantSelection(tenantId: string | undefined) {
  if (tenantId === undefined) {
    localStorage.setItem(TENANT_STORAGE_KEY, 'all')
  } else {
    localStorage.setItem(TENANT_STORAGE_KEY, tenantId)
  }
}